import { useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useLocation } from "wouter";
import { Layout } from "@/components/layout/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { DropdownMenu, DropdownMenuCheckboxItem, DropdownMenuContent, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import {
  ChevronDown,
  ChevronRight,
  RefreshCw,
  Mail,
  Loader2,
  Search,
  ArrowDownAZ,
  ExternalLink,
  X,
  Globe,
  Reply,
  Check,
  Filter,
  Users,
  MailSearch,
  Inbox as InboxIcon,
} from "lucide-react";
import { useAuth } from "@/lib/authContext";
import { cn } from "@/lib/utils";
import { EmailHtmlBody } from "@/components/custom/email/EmailHtmlBody";
import {
  api,
  emailApi,
  fetchWithAuth,
  type ArchivedEmail,
  type ClientEmail,
  type InboxGroup,
  type InboxMessage,
  type WaitingOnItem,
} from "@/lib/api";
import { fromInboxMessage, toReplyEmail, type UnifiedEmail } from "@/lib/email/types";
import { type EmailPill } from "@/lib/email/filters";
import { matchesPillSticky } from "@/lib/email/stickyPill";
import { filterHiddenGroups } from "@/lib/email/hiddenMailboxes";
import { useHiddenMailboxes } from "@/lib/userPrefs";
import { EmailRow } from "@/components/custom/email/EmailRow";
import { ClaimEmailDialog } from "@/components/custom/email/ClaimEmailDialog";
import { EmailForwardDialog } from "@/components/custom/email/EmailForwardDialog";
import { BulkEmailActionBar } from "@/components/custom/email/BulkEmailActionBar";
import { useEmailActions, type BulkEmailAction } from "@/components/custom/email/useEmailActions";
import { useEmailSelection } from "@/components/custom/email/useEmailSelection";
import { useLoadOlder } from "@/components/custom/email/useLoadOlder";
import { useStickyPill } from "@/components/custom/email/useStickyPill";
import InboxOutlook from "@/pages/InboxOutlook";
import { EmailReplyModal } from "@/components/custom/EmailReplyModal";
import { MailboxFilterRulesDialog } from "@/components/custom/MailboxFilterRulesDialog";
import { useToast } from "@/hooks/use-toast";

type FilterMode = "all" | "unread" | "mine" | "unclaimed" | "starred" | "waitingOn";

const FILTERS: { key: FilterMode; label: string }[] = [
  { key: "all", label: "All" },
  { key: "unread", label: "Unread" },
  { key: "mine", label: "Mine" },
  { key: "unclaimed", label: "Unclaimed" },
  { key: "starred", label: "Starred" },
  { key: "waitingOn", label: "Waiting On" },
];

/** W6 — group already-filtered messages into conversation threads for the Threaded view.
 * Singles (no conversationId, or the only message in that conversation) pass through untouched. */
function groupIntoThreads(messages: UnifiedEmail[]): { key: string; messages: UnifiedEmail[] }[] {
  const map = new Map<string, UnifiedEmail[]>();
  const order: string[] = [];
  for (const m of messages) {
    const key = m.conversationId ? `c:${m.conversationId}` : `m:${m.id}`;
    if (!map.has(key)) {
      map.set(key, []);
      order.push(key);
    }
    map.get(key)!.push(m);
  }
  const threads = order.map((key) => {
    const msgs = map.get(key)!;
    msgs.sort((a, b) => new Date(b.receivedDateTime).getTime() - new Date(a.receivedDateTime).getTime());
    return { key, messages: msgs };
  });
  threads.sort((a, b) => new Date(b.messages[0].receivedDateTime).getTime() - new Date(a.messages[0].receivedDateTime).getTime());
  return threads;
}

function shortDate(iso: string | null): string {
  if (!iso) return "";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "" : d.toLocaleDateString([], { month: "short", day: "numeric" });
}

/** Compact row for an archived email (search results / Waiting On) — no claim/pin/MH actions. */
function ArchivedRow({
  item,
  onReply,
  extra,
}: {
  item: ArchivedEmail;
  onReply: (item: ArchivedEmail) => void;
  extra?: React.ReactNode;
}) {
  const waiting = item as WaitingOnItem;
  const [expanded, setExpanded] = useState(false);
  const [body, setBody] = useState<{ content: string; contentType: string } | null>(null);
  const [bodyLoading, setBodyLoading] = useState(false);

  const toggleExpand = async () => {
    const next = !expanded;
    setExpanded(next);
    if (next && !body && !bodyLoading) {
      setBodyLoading(true);
      try {
        const full = await emailApi.getEmailBody(item.mailbox_address, item.graph_message_id);
        setBody(full.body ?? { content: item.body_preview ?? "", contentType: "text" });
      } catch {
        setBody({ content: item.body_preview ?? "(could not load message)", contentType: "text" });
      } finally {
        setBodyLoading(false);
      }
    }
  };

  return (
    <div className="border-b last:border-b-0">
      <div className="flex items-start gap-2 px-3 py-2 hover:bg-muted/30">
        <button onClick={toggleExpand} className="mt-0.5 text-muted-foreground hover:text-foreground" aria-label="Expand">
          {expanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
        </button>
        <div className="min-w-0 flex-1 cursor-pointer" onClick={toggleExpand}>
          <div className="flex items-center gap-1.5">
            {waiting.account_name && (
              <Badge variant="outline" className="shrink-0 text-[10px]">{waiting.account_name}</Badge>
            )}
            <span className="truncate text-sm font-medium">{item.subject || "(no subject)"}</span>
          </div>
          <div className="mt-0.5 flex items-center gap-2 text-xs text-muted-foreground">
            <span className="shrink-0 truncate max-w-[200px]">{item.from_name || item.from_email || "(unknown sender)"}</span>
            <span className="truncate">{item.body_preview}</span>
          </div>
          {waiting.follow_up_at && (
            <div className="mt-0.5 text-[11px] text-amber-700 dark:text-amber-500">
              Follow up {shortDate(waiting.follow_up_at)}
              {waiting.follow_up_note ? ` — ${waiting.follow_up_note}` : ""}
              {waiting.assignee_name ? ` · ${waiting.assignee_name}` : ""}
            </div>
          )}
        </div>
        <span className="shrink-0 text-[11px] text-muted-foreground">{shortDate(item.received_at)}</span>
        <Button variant="ghost" size="icon" className="h-7 w-7 shrink-0" title="Reply" onClick={() => onReply(item)}>
          <Reply className="h-4 w-4 text-blue-600" />
        </Button>
        {extra}
      </div>
      {expanded && (
        <div className="px-10 pb-3">
          {bodyLoading ? (
            <div className="flex items-center gap-2 py-3 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" /> Loading message…
            </div>
          ) : body ? (
            body.contentType === "html" ? (
              <EmailHtmlBody
                className="prose prose-sm max-w-none overflow-auto max-h-96 rounded border p-3"
                html={body.content}
              />
            ) : (
              <pre className="max-h-96 overflow-auto whitespace-pre-wrap rounded border bg-background p-3 text-sm text-muted-foreground">
                {body.content}
              </pre>
            )
          ) : null}
        </div>
      )}
    </div>
  );
}

export default function Inbox() {
  const { user } = useAuth();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [, navigate] = useLocation();
  const currentUserId = user?.id ?? "";

  const [filter, setFilter] = useState<FilterMode>("all");
  const [sortBy, setSortBy] = useState<"name" | "unread">("name");
  // W6 — threaded conversation view, default off (flat list unchanged). No other view pref
  // in this page persists to localStorage, so plain state matches convention.
  const [threaded, setThreaded] = useState(false);
  const [expandedThreads, setExpandedThreads] = useState<Set<string>>(new Set());
  const [search, setSearch] = useState("");
  const [overrides, setOverrides] = useState<Record<string, boolean>>({});

  // Client picker filter — searches the already-loaded group list, no extra fetch.
  const [clientFilter, setClientFilter] = useState<string | null>(null);
  const [clientPickerOpen, setClientPickerOpen] = useState(false);
  const [clientPickerSearch, setClientPickerSearch] = useState("");

  // Client Group filter — group→member account ids fetched on demand.
  const [groupFilterId, setGroupFilterId] = useState<string | null>(null);
  const [groupPickerOpen, setGroupPickerOpen] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [reply, setReply] = useState<{ email: ClientEmail; accountId: string; replyAll?: boolean } | null>(null);
  const [claimDialogEmail, setClaimDialogEmail] = useState<UnifiedEmail | null>(null);
  const [forwardEmail, setForwardEmail] = useState<UnifiedEmail | null>(null);

  // ENG-96: rows this page has optimistically patched stay listed until the pill or the
  // client/group scope changes (epoch), so opening an email under Unread cannot make its
  // row - or the whole group card - vanish mid-click.
  const sticky = useStickyPill(`${filter}|${clientFilter ?? "*"}|${groupFilterId ?? "*"}`);

  // Shared action hook — optimistic patches land on the ["inbox"] cache directly, so
  // rows update instantly without per-row local state or a full refetch.
  const actions = useEmailActions({ surface: "inbox" }, { onTouch: sticky.mark });

  // W3 — noise filters: which mailboxes' "Filtered" buckets are expanded, and the rules dialog target.
  const [expandedFiltered, setExpandedFiltered] = useState<Set<string>>(new Set());
  const [rulesDialogMailbox, setRulesDialogMailbox] = useState<string | null>(null);
  const [rulesDialogOpen, setRulesDialogOpen] = useState(false);

  const toggleFiltered = (mailbox: string) =>
    setExpandedFiltered((prev) => {
      const next = new Set(prev);
      next.has(mailbox) ? next.delete(mailbox) : next.add(mailbox);
      return next;
    });

  const toggleThread = (key: string) =>
    setExpandedThreads((prev) => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });

  const openRulesDialog = (mailbox: string | null) => {
    setRulesDialogMailbox(mailbox);
    setRulesDialogOpen(true);
  };

  // "Load older" pagination — per-mailbox pages beyond the main /api/inbox window, held
  // in component state (not React Query) so they survive the 60s poll. Shared with the
  // pop-out and the account page Shared Inbox tab: components/custom/email/useLoadOlder.
  const olderPages = useLoadOlder({
    logTag: "[inbox]",
    onError: () => toast({ title: "Failed to load older emails", variant: "destructive" }),
  });

  const loadOlder = (group: InboxGroup) =>
    olderPages.loadOlder([{ mailbox: group.mailbox_address, baseRows: group.messages }]);

  // W1 — Waiting On: "mine only" defaults on (Cassie wants HER list).
  const [waitingMineOnly, setWaitingMineOnly] = useState(true);

  // W2 — archive search is an ADDITIVE mode: only fires on submit (Enter / button), not per keystroke.
  const [activeSearch, setActiveSearch] = useState("");

  // Standalone pop-out window: chrome-less, no sidebar/nav.
  const isPopout = typeof window !== "undefined" && new URLSearchParams(window.location.search).get("popout") === "1";

  const openPopout = () => {
    window.open("/inbox?popout=1", "centcom-inbox", "width=520,height=900,menubar=no,toolbar=no");
  };

  const { data: allGroups = [], isLoading, refetch } = useQuery<InboxGroup[]>({
    queryKey: ["inbox"],
    queryFn: () => emailApi.getInbox(),
    refetchInterval: 60_000,
  });

  // Per-user mailbox visibility — hidden mailboxes are excluded from every aggregate
  // below (unread totals, sort, client picker) and from the rendered accordion.
  const [hiddenMailboxes, setMailboxHidden] = useHiddenMailboxes();
  const groups = useMemo(() => filterHiddenGroups(allGroups, hiddenMailboxes), [allGroups, hiddenMailboxes]);
  const [mailboxPickerOpen, setMailboxPickerOpen] = useState(false);

  const { data: users = [] } = useQuery({
    queryKey: ["users", "assignable"],
    queryFn: () => api.getUsers({ assignable: true }),
    staleTime: 5 * 60_000,
  });

  // Known Outlook category names: firm-wide distinct list + anything on the loaded window.
  const { data: categoryNamesData } = useQuery({
    queryKey: ["email-category-names"],
    queryFn: () => emailApi.getCategoryNames(),
    staleTime: Infinity,
  });
  const knownCategories = useMemo(() => {
    const names = new Set<string>(categoryNamesData?.names ?? []);
    for (const g of groups) for (const m of g.messages) for (const c of m.categories ?? []) names.add(c);
    return Array.from(names).sort((a, b) => a.localeCompare(b));
  }, [categoryNamesData, groups]);

  const { data: clientGroups = [] } = useQuery<{ id: string; name: string; client_count: number }[]>({
    queryKey: ["client-groups"],
    queryFn: () => fetchWithAuth("/api/client-groups"),
    staleTime: 5 * 60_000,
  });

  // Endpoint returns a bare array of member rows (see routes.ts GET /api/client-groups/:id/members)
  const { data: groupMembers } = useQuery<{ account_id: string }[]>({
    queryKey: ["client-group-members", groupFilterId],
    queryFn: () => fetchWithAuth(`/api/client-groups/${groupFilterId}/members`),
    enabled: !!groupFilterId,
  });
  const groupAccountIds = useMemo(
    () => (groupMembers ? new Set(groupMembers.map((m) => m.account_id)) : null),
    [groupMembers]
  );
  const selectedGroup = clientGroups.find((g) => g.id === groupFilterId) ?? null;

  // W1 — Waiting On flat list (durable follow-ups, survives Graph's recent-message window).
  const { data: waitingOn = [], isLoading: waitingLoading } = useQuery<WaitingOnItem[]>({
    queryKey: ["waiting-on", waitingMineOnly],
    queryFn: () => emailApi.getWaitingOn(waitingMineOnly),
    enabled: filter === "waitingOn",
  });

  // W2 — full-archive search, unlimited by date. Only runs when activeSearch is set.
  const { data: searchResults, isLoading: searchLoading } = useQuery<{ emails: ArchivedEmail[] }>({
    queryKey: ["archive-search", activeSearch],
    queryFn: () => emailApi.searchArchive(activeSearch),
    enabled: activeSearch.trim() !== "",
  });
  const isArchiveSearching = activeSearch.trim() !== "";

  const runArchiveSearch = () => {
    const q = search.trim();
    if (!q) return;
    setFilter("all"); // archive search and the Waiting On view are mutually exclusive
    setActiveSearch(q);
  };

  const clearArchiveSearch = () => {
    setActiveSearch("");
    setSearch("");
  };

  const totalUnread = useMemo(
    () => groups.reduce((sum, g) => sum + (g.unread || 0), 0),
    [groups]
  );

  const q = search.trim().toLowerCase();
  const filtering = filter !== "all" || q !== "";

  // Filter pills act on individual messages (shared predicate — also used by the
  // AccountDetail Shared Inbox tab). "waitingOn" renders its own view, never here.
  const pillMatch = (u: UnifiedEmail): boolean =>
    filter === "waitingOn"
      ? true
      : matchesPillSticky(u, filter as EmailPill, currentUserId, sticky.ids);

  // Search matches a message by subject/sender…
  const msgSearch = (u: UnifiedEmail): boolean => {
    if (!q) return true;
    const hay = `${u.subject ?? ""} ${u.fromName ?? ""} ${u.fromAddress ?? ""}`.toLowerCase();
    return hay.includes(q);
  };

  // …or the whole group when the query matches the client name / mailbox (jump-to-client).
  const groupNameMatches = (g: InboxGroup): boolean =>
    !!q && (g.account_name.toLowerCase().includes(q) || g.mailbox_address.toLowerCase().includes(q));

  // Default sort is client A–Z; "unread" puts the busiest mailboxes on top.
  const sortedGroups = useMemo(() => {
    const arr = [...groups];
    arr.sort((a, b) =>
      sortBy === "name"
        ? a.account_name.localeCompare(b.account_name)
        : b.unread - a.unread || a.account_name.localeCompare(b.account_name)
    );
    return arr;
  }, [groups, sortBy]);

  // Distinct clients present in the loaded inbox groups, for the client picker.
  const inboxClients = useMemo(() => {
    const map = new Map<string, string>();
    for (const g of groups) map.set(g.account_id, g.account_name);
    return [...map.entries()]
      .map(([account_id, account_name]) => ({ account_id, account_name }))
      .sort((a, b) => a.account_name.localeCompare(b.account_name));
  }, [groups]);
  const filteredClientOptions = inboxClients.filter((c) =>
    !clientPickerSearch.trim() || c.account_name.toLowerCase().includes(clientPickerSearch.toLowerCase())
  );
  const selectedClientName = inboxClients.find((c) => c.account_id === clientFilter)?.account_name ?? null;

  const displayGroups = useMemo(
    () =>
      sortedGroups.filter(
        (g) =>
          (!clientFilter || g.account_id === clientFilter) &&
          (!groupAccountIds || groupAccountIds.has(g.account_id))
      ),
    [sortedGroups, clientFilter, groupAccountIds]
  );

  const openReply = (u: UnifiedEmail, opts?: { replyAll?: boolean }) => {
    setReply({ email: toReplyEmail(u), accountId: u.accountId, replyAll: opts?.replyAll });
  };

  // Reply from the Waiting On / archive-search flat lists (no InboxGroup available there).
  const openReplyForArchived = (item: ArchivedEmail) => {
    const email: ClientEmail = {
      id: item.graph_message_id,
      subject: item.subject,
      bodyPreview: item.body_preview,
      receivedDateTime: item.received_at ?? new Date().toISOString(),
      hasAttachments: item.has_attachments,
      isRead: item.is_read,
      from: item.from_email ? { emailAddress: { name: item.from_name ?? "", address: item.from_email } } : null,
      toRecipients: [],
      ccRecipients: [],
      mailboxAddress: item.mailbox_address,
    };
    setReply({ email, accountId: item.account_id ?? "" });
  };

  const markWaitingDone = async (item: WaitingOnItem) => {
    try {
      await emailApi.setEmailFlag({ mailbox: item.mailbox_address, message_id: item.graph_message_id, flagged: false });
      queryClient.invalidateQueries({ queryKey: ["waiting-on"] });
    } catch (err: any) {
      toast({ title: "Could not update", description: err.message, variant: "destructive" });
    }
  };

  const onRefresh = async () => {
    setRefreshing(true);
    sticky.reset(); // ENG-96 - explicit refresh re-evaluates every row against the pill
    selection.clear(); // a refresh can reorder/evict rows - never act on a stale selection
    try {
      // Bypass the server cache, then let the query cache update.
      await emailApi.getInbox(true);
      await refetch();
    } finally {
      setRefreshing(false);
    }
  };

  const isOpen = (g: InboxGroup) =>
    filtering ? true : overrides[g.mailbox_address] ?? false;

  const toggleGroup = (mb: string, current: boolean) =>
    setOverrides((prev) => ({ ...prev, [mb]: !current }));

  // Per-group rows, hoisted out of the JSX so the bulk selection can derive its ordered id
  // list from EXACTLY what gets rendered, in the same order — the range a shift-click spans
  // has to be the range the user can see. Plain computation (no memo) matching this page's
  // style: it is array work over a ≤100-message window per mailbox, and every input below
  // changes on most renders anyway.
  const groupRows = displayGroups.map((group) => {
    const nameHit = groupNameMatches(group);
    const olderMsgs = olderPages.rowsByMailbox[group.mailbox_address] ?? [];
    const unified = [...group.messages, ...olderMsgs].map((m) => fromInboxMessage(m, group));
    const visible = unified.filter((u) => pillMatch(u) && (nameHit || msgSearch(u)));
    const hidden = filtering && visible.length === 0 && !nameHit;
    const open = nameHit ? true : isOpen(group);
    const threads = threaded ? groupIntoThreads(visible) : null;
    // Rows actually on screen right now. A closed group and a collapsed thread contribute
    // nothing on purpose — a selection the user cannot see is the failure mode being
    // designed out (see lib/email/selection.ts).
    const rendered: UnifiedEmail[] =
      hidden || !open
        ? []
        : threads
          ? threads.flatMap((t) =>
              t.messages.length === 1 || expandedThreads.has(t.key) ? t.messages : [],
            )
          : visible;
    return { group, nameHit, visible, threads, hidden, open, rendered };
  });

  const orderedVisibleIds = groupRows.flatMap((g) => g.rendered.map((u) => u.id));
  const emailById = new Map(groupRows.flatMap((g) => g.rendered).map((u) => [u.id, u]));

  // Bulk multi-select. Epoch = pill + client/group scope + threaded/archive-search MODE —
  // i.e. everything that changes which list the user is looking at. Deliberately NOT the
  // live search text: a keystroke narrows the same list, and `visibleSelected` already
  // prunes anything that scrolled out of the filter.
  const selection = useEmailSelection(
    orderedVisibleIds,
    `${filter}|${clientFilter ?? "*"}|${groupFilterId ?? "*"}|${threaded}|${isArchiveSearching}`,
  );

  const runBulk = async (action: BulkEmailAction) => {
    const emails = selection.visibleSelected
      .map((id) => emailById.get(id))
      .filter((e): e is UnifiedEmail => !!e);
    if (emails.length === 0) return;
    const { failed } = await actions.bulkApply(emails, action);
    // Leave the selection intact on a partial failure so the user can retry the same rows.
    if (failed === 0) selection.clear();
  };

  const body = (
    <>
      <div className="mx-auto max-w-5xl p-4 md:p-6">
        {/* Header */}
        <div className="mb-4 flex items-center gap-3">
          <Mail className="h-6 w-6 text-primary" />
          <div>
            <h1 className="text-2xl font-bold">Shared Inbox</h1>
            <p className="text-sm text-muted-foreground">
              {groups.length} client {groups.length === 1 ? "mailbox" : "mailboxes"} · {totalUnread} unread
            </p>
          </div>
          {!isPopout && (
            <Button
              size="sm"
              className="gap-2 bg-violet-600 text-white hover:bg-violet-700"
              onClick={openPopout}
            >
              <ExternalLink className="h-4 w-4" />
              Pop out
            </Button>
          )}
          <Button
            size="sm"
            className="gap-2 bg-emerald-600 text-white hover:bg-emerald-700"
            onClick={onRefresh}
            disabled={refreshing}
          >
            <RefreshCw className={cn("h-4 w-4", refreshing && "animate-spin")} />
            Refresh
          </Button>
        </div>

        {/* Toolbar */}
        <div className="mb-4 space-y-2">
          {/* Row 1: filter pills */}
          <div className="flex flex-wrap gap-2">
            {FILTERS.map((f) => (
              <Button
                key={f.key}
                size="sm"
                variant={filter === f.key ? "default" : "outline"}
                className="h-8"
                onClick={() => {
                  setActiveSearch(""); // pills and archive search are mutually exclusive views
                  setFilter(f.key);
                }}
              >
                {f.label}
              </Button>
            ))}
          </div>

          {/* Row 2: client/group pickers + sort + threaded */}
          <div className="flex flex-wrap items-center gap-2">
            <Popover open={clientPickerOpen} onOpenChange={(o) => { setClientPickerOpen(o); if (!o) setClientPickerSearch(""); }}>
              <PopoverTrigger asChild>
                <Button variant="outline" size="sm" className="h-8 w-40 shrink-0 justify-between gap-1 font-normal">
                  <span className={cn("truncate", !selectedClientName && "text-muted-foreground")}>
                    {selectedClientName ?? "All clients"}
                  </span>
                  {selectedClientName ? (
                    <X
                      className="h-3.5 w-3.5 shrink-0 text-muted-foreground hover:text-foreground"
                      onClick={(e) => { e.stopPropagation(); setClientFilter(null); }}
                    />
                  ) : (
                    <ChevronDown className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                  )}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-64 p-2" align="start">
                <Input
                  autoFocus
                  placeholder="Search clients…"
                  value={clientPickerSearch}
                  onChange={(e) => setClientPickerSearch(e.target.value)}
                  className="mb-2"
                />
                <div className="-mx-2 max-h-64 overflow-y-auto">
                  {filteredClientOptions.length === 0 ? (
                    <p className="px-3 py-2 text-sm text-muted-foreground">No clients found.</p>
                  ) : (
                    filteredClientOptions.map((c) => (
                      <button
                        key={c.account_id}
                        className="w-full rounded px-3 py-1.5 text-left text-sm hover:bg-muted/50"
                        onClick={() => { setClientFilter(c.account_id); setClientPickerOpen(false); }}
                      >
                        {c.account_name}
                      </button>
                    ))
                  )}
                </div>
              </PopoverContent>
            </Popover>
            <Popover open={groupPickerOpen} onOpenChange={setGroupPickerOpen}>
              <PopoverTrigger asChild>
                <Button variant="outline" size="sm" className="h-8 w-40 shrink-0 justify-between gap-1 font-normal">
                  <span className={cn("truncate", !selectedGroup && "text-muted-foreground")}>
                    {selectedGroup ? `Group: ${selectedGroup.name}` : "Client group"}
                  </span>
                  {selectedGroup ? (
                    <X
                      className="h-3.5 w-3.5 shrink-0 text-muted-foreground hover:text-foreground"
                      onClick={(e) => { e.stopPropagation(); setGroupFilterId(null); }}
                    />
                  ) : (
                    <Users className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                  )}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-64 p-2" align="start">
                <div className="-mx-2 max-h-64 overflow-y-auto">
                  {clientGroups.length === 0 ? (
                    <p className="px-3 py-2 text-sm text-muted-foreground">No client groups.</p>
                  ) : (
                    clientGroups.map((g) => (
                      <button
                        key={g.id}
                        className="w-full rounded px-3 py-1.5 text-left text-sm hover:bg-muted/50"
                        onClick={() => { setGroupFilterId(g.id); setGroupPickerOpen(false); }}
                      >
                        {g.name} <span className="text-xs text-muted-foreground">({g.client_count})</span>
                      </button>
                    ))
                  )}
                </div>
              </PopoverContent>
            </Popover>
            <Button
              size="sm"
              variant="outline"
              className="h-8 shrink-0 gap-1.5"
              onClick={() => setSortBy((s) => (s === "name" ? "unread" : "name"))}
              title="Toggle sort order"
            >
              <ArrowDownAZ className="h-4 w-4" />
              {sortBy === "name" ? "Client A–Z" : "Most unread"}
            </Button>
            <Button
              size="sm"
              variant={threaded ? "default" : "outline"}
              className="h-8 shrink-0 gap-1.5"
              onClick={() => setThreaded((v) => !v)}
              title="Group messages into conversation threads"
            >
              Threaded
            </Button>
            <DropdownMenu open={mailboxPickerOpen} onOpenChange={setMailboxPickerOpen}>
              <DropdownMenuTrigger asChild>
                <Button size="sm" variant="outline" className="h-8 shrink-0 gap-1.5">
                  <InboxIcon className="h-4 w-4" />
                  Choose mailboxes ({allGroups.length - allGroups.filter((g) => hiddenMailboxes.has(g.mailbox_address.toLowerCase())).length} of {allGroups.length} shown)
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="max-h-80 w-72 overflow-y-auto">
                {allGroups.length === 0 ? (
                  <div className="px-2 py-1.5 text-sm text-muted-foreground">No mailboxes.</div>
                ) : (
                  allGroups.map((g) => (
                    <DropdownMenuCheckboxItem
                      key={g.mailbox_address}
                      checked={!hiddenMailboxes.has(g.mailbox_address.toLowerCase())}
                      onCheckedChange={(checked) => setMailboxHidden(g.mailbox_address, !checked)}
                      onSelect={(e) => e.preventDefault()}
                    >
                      <span className="truncate">
                        {g.account_name}{" "}
                        <span className="text-xs text-muted-foreground">({g.mailbox_address})</span>
                      </span>
                    </DropdownMenuCheckboxItem>
                  ))
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>

          {/* Row 3: search + archive search + filter rules */}
          <div className="flex items-center gap-2">
            <div className="relative min-w-0 flex-1">
              <Search className="absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && runArchiveSearch()}
                placeholder="Search subject or client…"
                className="pl-8"
              />
            </div>
            <Button
              size="sm"
              variant="outline"
              className="h-8 shrink-0 gap-1.5"
              onClick={runArchiveSearch}
              disabled={!search.trim()}
              title="Search the full email archive (unlimited by date)"
            >
              <Globe className="h-4 w-4" />
              Search all mail
            </Button>
            <Button
              size="sm"
              variant="outline"
              className="h-8 shrink-0 gap-1.5"
              onClick={() => openRulesDialog(null)}
              title="Manage firm-wide noise filter rules"
            >
              <Filter className="h-4 w-4" />
              Filter rules
            </Button>
          </div>
        </div>

        {/* Bulk multi-select bar. Scoped to the grouped mailbox list: the Waiting On and
            archive-search views render ArchivedRow, which carries no checkbox. */}
        {/* Count = selection ∩ visible — collapsing a group or typing a search hides rows the
            action will not reach, and a bar claiming them would silently under-act, then clear
            the whole selection (review finding, v1.5.62). */}
        {selection.visibleSelected.length > 0 && !isArchiveSearching && filter !== "waitingOn" && (
          <div className="sticky top-2 z-10 mb-3">
            <BulkEmailActionBar
              count={selection.visibleSelected.length}
              users={users}
              currentUserId={currentUserId}
              knownCategories={knownCategories}
              busy={actions.bulkBusy}
              onMarkRead={(isRead) => runBulk({ type: "read", isRead })}
              onAssign={(u) => runBulk({ type: "assign", user: u })}
              onFlag={(flagged) => runBulk({ type: "flag", flagged })}
              onCategorize={(label) => runBulk({ type: "categorize", label })}
              onClear={selection.clear}
            />
          </div>
        )}

        {/* Body */}
        {filter === "waitingOn" ? (
          <div className="space-y-3">
            <div className="flex items-center justify-between rounded-lg border bg-card px-3 py-2">
              <span className="text-sm text-muted-foreground">
                {waitingOn.length} open follow-up{waitingOn.length === 1 ? "" : "s"}
              </span>
              <Button
                size="sm"
                variant={waitingMineOnly ? "default" : "outline"}
                className="h-7"
                onClick={() => setWaitingMineOnly((v) => !v)}
              >
                Mine only
              </Button>
            </div>
            {waitingLoading ? (
              <div className="flex items-center justify-center gap-2 py-16 text-muted-foreground">
                <Loader2 className="h-5 w-5 animate-spin" /> Loading Waiting On…
              </div>
            ) : waitingOn.length === 0 ? (
              <div className="rounded-lg border border-dashed py-16 text-center text-muted-foreground">
                Nothing waiting on a reply. Star an email to add it here.
              </div>
            ) : (
              <div className="overflow-hidden rounded-lg border bg-card">
                {waitingOn.map((item) => (
                  <ArchivedRow
                    key={item.id}
                    item={item}
                    onReply={openReplyForArchived}
                    extra={
                      <Button variant="ghost" size="icon" className="h-7 w-7 shrink-0" title="Mark done" onClick={() => markWaitingDone(item)}>
                        <Check className="h-4 w-4 text-green-600" />
                      </Button>
                    }
                  />
                ))}
              </div>
            )}
          </div>
        ) : isArchiveSearching ? (
          <div className="space-y-3">
            <div className="flex items-center justify-between rounded-lg border bg-card px-3 py-2">
              <span className="text-sm text-muted-foreground">
                {searchLoading ? "Searching…" : `${searchResults?.emails.length ?? 0} result(s) for "${activeSearch}"`}
              </span>
              <Button size="sm" variant="outline" className="h-7 gap-1" onClick={clearArchiveSearch}>
                <X className="h-3.5 w-3.5" />
                Clear
              </Button>
            </div>
            {searchLoading ? (
              <div className="flex items-center justify-center gap-2 py-16 text-muted-foreground">
                <Loader2 className="h-5 w-5 animate-spin" /> Searching archive…
              </div>
            ) : !searchResults || searchResults.emails.length === 0 ? (
              <div className="rounded-lg border border-dashed py-16 text-center text-muted-foreground">
                No archived email matches "{activeSearch}".
              </div>
            ) : (
              <div className="overflow-hidden rounded-lg border bg-card">
                {searchResults.emails.map((item) => (
                  <ArchivedRow key={item.id} item={item} onReply={openReplyForArchived} />
                ))}
              </div>
            )}
          </div>
        ) : isLoading ? (
          <div className="flex items-center justify-center gap-2 py-16 text-muted-foreground">
            <Loader2 className="h-5 w-5 animate-spin" /> Loading inbox…
          </div>
        ) : allGroups.length === 0 ? (
          <div className="rounded-lg border border-dashed py-16 text-center text-muted-foreground">
            No client mailboxes are mapped yet. Map a mailbox to a client in Settings → Email.
          </div>
        ) : groups.length === 0 ? (
          <div className="rounded-lg border border-dashed py-16 text-center text-muted-foreground">
            All mailboxes hidden — use the Mailboxes menu to show some.
          </div>
        ) : (
          <div className="space-y-3">
            {groupRows.map(({ group, visible, threads, hidden, open }) => {
              if (hidden) return null;
              const rowProps = {
                variant: "inbox" as const,
                currentUserId,
                users,
                makershubForwardConfigured: group.makershubForwardConfigured,
                knownCategories,
                actions,
                onReply: openReply,
                onForward: setForwardEmail,
                onOpenClaimDialog: setClaimDialogEmail,
                // Same far-left Claim/Assigned pill as the account Shared Inbox tab (opens
                // the claim-with-notes dialog) — jmenzel's ask after the v1.5.14 deploy.
                prominentClaim: true,
                // Contractors can't send as a shared mailbox (reply/forward hidden).
                canSend: user?.role !== "contractor",
              };
              // Bulk-select props for one row. Kept out of rowProps on purpose: the Filtered
              // (noise) bucket below reuses rowProps and must stay unselectable.
              const selectProps = (u: UnifiedEmail) => ({
                selectable: true,
                selected: selection.isSelected(u.id),
                selectionActive: selection.active,
                onToggleSelect: (shiftKey: boolean) => selection.toggle(u.id, shiftKey),
              });
              return (
                <div key={group.mailbox_address} className="overflow-hidden rounded-lg border bg-card">
                  {/* Group header */}
                  <button
                    onClick={() => toggleGroup(group.mailbox_address, open)}
                    disabled={filtering}
                    className="flex w-full items-center gap-2 px-3 py-2.5 text-left hover:bg-muted/50 disabled:cursor-default"
                  >
                    {open ? <ChevronDown className="h-4 w-4 shrink-0" /> : <ChevronRight className="h-4 w-4 shrink-0" />}
                    <span className="truncate font-semibold">{group.account_name}</span>
                    <span className="truncate text-xs text-muted-foreground">{group.mailbox_address}</span>
                    {group.unread > 0 && (
                      <Badge className="ml-auto shrink-0 bg-blue-600 hover:bg-blue-600">{group.unread} unread</Badge>
                    )}
                    <span className={cn("shrink-0 text-xs text-muted-foreground", group.unread > 0 ? "ml-2" : "ml-auto")}>
                      {filtering ? `${visible.length} shown` : `${group.total} recent`}
                    </span>
                    <span
                      role="button"
                      tabIndex={0}
                      title="Search this mailbox's full archive"
                      className="ml-1 shrink-0 rounded p-1 text-muted-foreground hover:bg-muted hover:text-foreground"
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(`/email-search?mailbox=${encodeURIComponent(group.mailbox_address)}`);
                      }}
                    >
                      <MailSearch className="h-3.5 w-3.5" />
                    </span>
                    <span
                      role="button"
                      tabIndex={0}
                      title="Filter rules for this mailbox"
                      className="ml-1 shrink-0 rounded p-1 text-muted-foreground hover:bg-muted hover:text-foreground"
                      onClick={(e) => {
                        e.stopPropagation();
                        openRulesDialog(group.mailbox_address);
                      }}
                    >
                      <Filter className="h-3.5 w-3.5" />
                    </span>
                  </button>

                  {/* Rows */}
                  {open && (
                    <div className="border-t">
                      {visible.length === 0 ? (
                        <div className="px-4 py-3 text-sm text-muted-foreground">No messages.</div>
                      ) : !threads ? (
                        visible.map((u) => <EmailRow key={u.id} email={u} {...rowProps} {...selectProps(u)} />)
                      ) : (
                        threads.map((thread) => {
                          if (thread.messages.length === 1) {
                            const u = thread.messages[0];
                            return <EmailRow key={u.id} email={u} {...rowProps} {...selectProps(u)} />;
                          }
                          const threadOpen = expandedThreads.has(thread.key);
                          const threadUnread = thread.messages.some((m) => !m.isRead);
                          return (
                            <div key={thread.key} className="border-b last:border-b-0">
                              <button
                                onClick={() => toggleThread(thread.key)}
                                className="flex w-full items-center gap-2 px-3 py-2 text-left hover:bg-muted/30"
                              >
                                {threadOpen ? (
                                  <ChevronDown className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                                ) : (
                                  <ChevronRight className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                                )}
                                {threadUnread && <span className="h-2 w-2 shrink-0 rounded-full bg-blue-600" />}
                                <span className="truncate text-sm font-medium">
                                  {thread.messages[0].subject || "(no subject)"}
                                </span>
                                <span className="ml-auto shrink-0 text-xs text-muted-foreground">
                                  {thread.messages.length} messages
                                </span>
                                <span className="shrink-0 text-[11px] text-muted-foreground">
                                  {shortDate(thread.messages[0].receivedDateTime)}
                                </span>
                              </button>
                              {threadOpen && (
                                <div className="border-t border-dashed pl-4">
                                  {thread.messages.map((u) => (
                                    <EmailRow key={u.id} email={u} {...rowProps} {...selectProps(u)} />
                                  ))}
                                </div>
                              )}
                            </div>
                          );
                        })
                      )}
                      {/* W3 — noise-filtered messages, set aside not deleted, collapsed by default */}
                      {!!group.filteredCount && (
                        <div className="border-t bg-muted/20">
                          <button
                            onClick={() => toggleFiltered(group.mailbox_address)}
                            className="flex w-full items-center gap-2 px-3 py-2 text-left text-xs text-muted-foreground hover:bg-muted/40"
                          >
                            {expandedFiltered.has(group.mailbox_address) ? (
                              <ChevronDown className="h-3.5 w-3.5 shrink-0" />
                            ) : (
                              <ChevronRight className="h-3.5 w-3.5 shrink-0" />
                            )}
                            Filtered ({group.filteredCount})
                          </button>
                          {expandedFiltered.has(group.mailbox_address) && (
                            <div className="border-t opacity-70">
                              {(group.filtered ?? []).map((m) => (
                                <EmailRow key={m.id} email={fromInboxMessage(m, group)} {...rowProps} />
                              ))}
                            </div>
                          )}
                        </div>
                      )}
                      {/* "Load older" — pages a single mailbox back beyond the main /api/inbox
                          window via the retention archive; local state so it survives the 60s poll. */}
                      {!olderPages.isExhausted(group.mailbox_address) && (
                        <div className="border-t px-3 py-2 text-center">
                          <Button
                            size="sm"
                            variant="ghost"
                            className="h-7 text-xs text-muted-foreground"
                            disabled={olderPages.isLoading(group.mailbox_address)}
                            onClick={() => loadOlder(group)}
                          >
                            {olderPages.isLoading(group.mailbox_address) ? (
                              <span className="flex items-center gap-1.5">
                                <Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading older…
                              </span>
                            ) : (
                              "Load older"
                            )}
                          </Button>
                        </div>
                      )}
                      {olderPages.isExhausted(group.mailbox_address) && (
                        <div className="border-t px-3 py-2 text-center text-xs text-muted-foreground">
                          No older emails.
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Single reply modal owned by the page */}
      <EmailReplyModal
        email={reply?.email ?? null}
        accountId={reply?.accountId ?? ""}
        open={!!reply}
        onClose={() => setReply(null)}
        onSent={() => refetch()}
        replyAll={reply?.replyAll}
      />

      {/* Claim with notes + route-to-project (shared with the account page) */}
      <ClaimEmailDialog
        email={claimDialogEmail}
        users={users}
        actions={actions}
        onClose={() => setClaimDialogEmail(null)}
      />

      {/* Forward to arbitrary recipients (shared with the pop-out) */}
      <EmailForwardDialog email={forwardEmail} onClose={() => setForwardEmail(null)} />

      {/* W3 — noise filter rules management */}
      <MailboxFilterRulesDialog
        mailbox={rulesDialogMailbox}
        open={rulesDialogOpen}
        onClose={() => setRulesDialogOpen(false)}
      />
    </>
  );

  if (isPopout) {
    return <InboxOutlook />;
  }

  return <Layout>{body}</Layout>;
}
